import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { blogFormSchema, BlogFormData } from './schemas/blogFormSchema';
import JoditEditor from 'jodit-react';
import styles from './blogForm.module.css';
import ImageInput from './ImageInput';
import { uploadImageToFirebase } from '../libs/utils';

interface BlogFormProps {
  onSubmit: (data: BlogFormData) => void;
  isLoading?: boolean;
  defaultValues?: Partial<BlogFormData>;
}

const BlogForm: React.FC<BlogFormProps> = ({ onSubmit, isLoading = false, defaultValues }) => {
  const editor = useRef(null);
    const config = useMemo(
        () => ({
            readonly: false,
            placeholder: 'Start typing Blog description...',
            height: 300,
            uploader: {
                insertImageAsBase64URI: true, 
              }
        }),
        []
    );
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BlogFormData>({
    resolver: zodResolver(blogFormSchema),
    defaultValues: defaultValues || {},
  });

  const thumbnailImage = watch('thumbnailImage');
  const images = watch('images');
  const blogsImagesFirst = watch('blogsImagesFirst');
  const blogsImagesSecond = watch('blogsImagesSecond');

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setValue('thumbnailImage', await uploadImageToFirebase(file,'blog-images'));
  };

  const handleImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setValue('images', [await uploadImageToFirebase(e.target.files[0],'blog-images')]);
    }
  };

  const insertNewsImage = async (label: 'blogsImagesFirst' | 'blogsImagesSecond', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageBlob = await uploadImageToFirebase(file,'blog-images');
      setValue(label, imageBlob);
    }
  };

  const onFormSubmit = (data: BlogFormData) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className={styles.formContainer}>
      <div className={styles.formrow}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor='title'>Title</label>
          <input {...register('title')} placeholder="Title" id='title'/>
          {errors.title && <p className={styles.errorMessage}>{errors.title.message}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor='heading'>Heading</label>
          <input {...register('heading')} placeholder="Heading" id='heading'/>
          {errors.heading && <p className={styles.errorMessage}>{errors.heading.message}</p>}
        </div>
      </div>

      <div className={styles.formrow}>
        <div className={styles.formGroup}>
          <ImageInput id="thumbnail" label="Thumbnail" onChange={handleThumbnailChange} previewUrls={watch('thumbnailImage') ? [watch("thumbnailImage")] : undefined}/>
          {errors.thumbnailImage && <p className={styles.errorMessage}>{errors.thumbnailImage.message}</p>}

          <ImageInput id="images" label="Images" multiple onChange={handleImagesChange} 
          previewUrls={watch("images")?.filter((url):url is string => typeof url === 'string')}/>
          {errors.images && <p className={styles.errorMessage}>{errors.images.message}</p>}

          <ImageInput id="first" label="News Image 1" 
          onChange={(e) => insertNewsImage('blogsImagesFirst', e)} 
          previewUrls={watch("blogsImagesFirst") 
          ? [watch("blogsImagesFirst")].filter((url): url is string => typeof url === 'string') 
          : undefined }/>
          <ImageInput
            id="second"
            label="News Image 2"
            onChange={(e) => insertNewsImage('blogsImagesSecond', e)}
            previewUrls={
              watch("blogsImagesSecond")
                ? [watch("blogsImagesSecond")].filter((url): url is string => typeof url === 'string')
                : undefined
            }
          />
        </div>
      </div>

      <div className={styles.formrow}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor='shortdescription'>Short Description</label>
          <input {...register('shortdescription')} placeholder="Short Description" id='shortdescription'/>
          {errors.shortdescription && <p className={styles.errorMessage}>{errors.shortdescription.message}</p>}
        </div>
      </div>

      <div className={styles.formGroup}>
        <JoditEditor
          ref={editor}
          value={watch('description')}
          config={config}
          onBlur={(newContent: string) => setValue('description', newContent)}
          onChange={(newContent: string) => setValue('description', newContent)}
        />
        {errors.description && <p className={styles.errorMessage}>{errors.description.message}</p>}
      </div>

      <button type="submit" className={styles.submitButton} disabled={isLoading}>
        {isLoading ? 'Saving...' : defaultValues ? 'Update Blog' : 'Create Blog'}
      </button>
    </form>
  );
};

export default BlogForm;
